<template>
  <div class="receipt-container">
    <Toast />

    <!-- Loading State -->
    <div v-if="loading" class="loading-overlay">
      <i class="pi pi-spin pi-spinner" style="font-size: 2rem"></i>
      <p>Memuat data...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="error-container">
      <p class="error-message">{{ error }}</p>
      <Button
        label="Coba Lagi"
        @click="retryLoadData"
        class="p-button-primary"
      />
    </div>

    <!-- Content (only show when data is ready) -->
    <div v-else-if="isDataReady">
      <div
        v-for="(pageData, pageIndex) in paginatedData"
        :key="pageIndex"
        class="receipt-page"
      >
        <ReceiptHeader type="outlet" :data="outlet" />
        <div class="receipt-content">
          <div class="info-section">
            <div class="info-row">
              <div class="info-label">Tanggal:</div>
              <div class="info-value">
                {{ dateFormatter($route.query.start_date) }} s.d.
                {{ dateFormatter($route.query.end_date) }}
              </div>
            </div>
          </div>
          <div class="header-container">
            <h3 class="title"><b>LAPORAN PENJUALAN HARIAN BY CATEGORY</b></h3>
          </div>
          <div class="outgoing-detail">
            <div class="card print" id="printMe">
              <table class="detail-bon">
                <thead>
                  <tr>
                    <th>No</th>
                    <th>Kode Kategori Parent</th>
                    <th>Nama Kategori Parent</th>
                    <th>Total Qty</th>
                    <th>Total Item Amount (Rp)</th>
                    <th>Total Diskon Item (Rp)</th>
                    <th>Total Diskon (Rp)</th>
                    <th>Total Infaq (Rp)</th>
                    <th>Total Pembulatan (Rp)</th>
                    <th>Total Voucher (Rp)</th>
                    <th>Total Omset (Rp)</th>
                    <th>Jumlah Transaksi</th>
                  </tr>
                </thead>
                <tbody>
                  <!-- Parent Categories Section -->
                  <tr
                    v-for="(category, index) in pageData.categories"
                    :key="category.parent_category_id"
                  >
                    <td>{{ pageData.startIndex + index + 1 }}</td>
                    <td>{{ category.parent_category_code }}</td>
                    <td>{{ category.parent_category_name }}</td>
                    <td class="text-right">
                      {{ number_format(category.total_qty, 0, ',', '.') }}
                    </td>
                    <td class="text-right">
                      {{
                        number_format(category.total_item_amount, 0, ',', '.')
                      }}
                    </td>
                    <td class="text-right">
                      {{
                        number_format(category.total_discount_item, 0, ',', '.')
                      }}
                    </td>
                    <td class="text-right">
                      {{ number_format(category.total_discount, 0, ',', '.') }}
                    </td>
                    <td class="text-right">
                      {{ number_format(category.total_infaq, 0, ',', '.') }}
                    </td>
                    <td class="text-right">
                      {{ number_format(category.total_round_up, 0, ',', '.') }}
                    </td>
                    <td class="text-right">
                      {{ number_format(category.total_voucher, 0, ',', '.') }}
                    </td>
                    <td class="text-right">
                      <strong>{{
                        number_format(category.total_omset, 0, ',', '.')
                      }}</strong>
                    </td>
                    <td class="text-right">
                      {{
                        number_format(category.transaction_count, 0, ',', '.')
                      }}
                    </td>
                  </tr>
                  <!-- Summary row on last page -->
                  <tr
                    v-if="pageIndex + 1 === paginatedData.length"
                    class="total-row"
                  >
                    <td colspan="3"><b>TOTAL</b></td>
                    <td class="text-right">
                      <b>{{ number_format(totals.total_qty, 0, ',', '.') }}</b>
                    </td>
                    <td class="text-right">
                      <b>{{
                        number_format(totals.total_item_amount, 0, ',', '.')
                      }}</b>
                    </td>
                    <td class="text-right">
                      <b>{{
                        number_format(totals.total_discount_item, 0, ',', '.')
                      }}</b>
                    </td>
                    <td class="text-right">
                      <b>{{
                        number_format(totals.total_discount, 0, ',', '.')
                      }}</b>
                    </td>
                    <td class="text-right">
                      <b>{{
                        number_format(totals.total_infaq, 0, ',', '.')
                      }}</b>
                    </td>
                    <td class="text-right">
                      <b>{{
                        number_format(totals.total_round_up, 0, ',', '.')
                      }}</b>
                    </td>
                    <td class="text-right">
                      <b>{{
                        number_format(totals.total_voucher, 0, ',', '.')
                      }}</b>
                    </td>
                    <td class="text-right">
                      <b>{{
                        number_format(totals.total_omset, 0, ',', '.')
                      }}</b>
                    </td>
                    <td class="text-right">
                      <b>{{
                        number_format(totals.total_transactions, 0, ',', '.')
                      }}</b>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <!-- Subcategories Breakdown Section (only on last page) -->
            <div
              v-if="pageIndex + 1 === paginatedData.length"
              class="subcategories-section"
            >
              <h4 class="subcategories-title">
                <b>BREAKDOWN SUBCATEGORIES</b>
              </h4>
              <table class="detail-bon subcategories-table">
                <thead>
                  <tr>
                    <th>Kode Kategori Parent</th>
                    <th>Nama Kategori Parent</th>
                    <th>Kode Subcategory</th>
                    <th>Nama Subcategory</th>
                    <th>Total Qty</th>
                    <th>Total Omset (Rp)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="row in getAllSubcategoryRows()"
                    :key="
                      'subcat-' +
                      row.category.parent_category_id +
                      '-' +
                      row.subcategory.category_id
                    "
                    class="subcategory-row"
                  >
                    <td>{{ row.category.parent_category_code }}</td>
                    <td>{{ row.category.parent_category_name }}</td>
                    <td>{{ row.subcategory.category_code }}</td>
                    <td>{{ row.subcategory.category_name }}</td>
                    <td class="text-right">
                      {{
                        number_format(row.subcategory.total_qty, 0, ',', '.')
                      }}
                    </td>
                    <td class="text-right">
                      <strong>{{
                        number_format(row.subcategory.total_omset, 0, ',', '.')
                      }}</strong>
                    </td>
                  </tr>
                  <tr
                    v-if="getAllSubcategoryRows().length === 0"
                    class="subcategory-row"
                  >
                    <td colspan="6" class="text-center text-muted">
                      Tidak ada subcategory
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <!-- Payment Method Summary on last page -->
            <div
              v-if="pageIndex + 1 === paginatedData.length"
              class="payment-summary"
            >
              <h4>Ringkasan Metode Pembayaran</h4>
              <table class="summary-table">
                <thead>
                  <tr>
                    <th>Metode Pembayaran</th>
                    <th>Jumlah</th>
                  </tr>
                </thead>
                <tbody>
                  <template v-for="(item, k) in payment_method">
                    <tr v-if="item.name_payment != 'DISCOUNT'" :key="k">
                      <td>
                        <strong>{{ item.name_payment }}</strong>
                      </td>
                      <td class="text-right">
                        {{ number_format(item.total_payment, 0, ',', '.') }}
                      </td>
                    </tr>
                  </template>
                  <tr>
                    <td><strong>DISCOUNT CASHIER</strong></td>
                    <td class="text-right">
                      {{ number_format(totals.total_discount, 0, ',', '.') }}
                    </td>
                  </tr>
                  <tr>
                    <td><strong>DISCOUNT ITEM</strong></td>
                    <td class="text-right">
                      {{
                        number_format(totals.total_discount_item, 0, ',', '.')
                      }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ReceiptHeader from '@/components/receipt/ReceiptHeader.vue'
var moment = require('moment')

export default {
  name: 'DailyOmsetByCategoryReceipt',
  components: {
    ReceiptHeader
  },
  data() {
    return {
      // Constants
      CATEGORIES_PER_PAGE: 15,
      PRINT_DELAY: 1000,

      // State
      today: new Date().toISOString().split('T')[0],
      moment: moment,
      loading: false,
      error: null,
      categoryOmset: [],
      payment_method: [],
      totals: {
        total_qty: 0,
        total_item_amount: 0,
        total_discount_item: 0,
        total_discount: 0,
        total_infaq: 0,
        total_round_up: 0,
        total_voucher: 0,
        total_omset: 0,
        total_transactions: 0
      },
      outlet: null,
      paginatedData: [],
      printTimeoutId: null
    }
  },
  computed: {
    isDataReady() {
      return (
        this.categoryOmset.length > 0 &&
        this.outlet !== null &&
        !this.loading &&
        !this.error
      )
    }
  },
  async mounted() {
    // Validate route params first
    if (!this.validateRouteParams()) {
      return
    }

    this.loading = true
    this.error = null

    try {
      // Fetch data in parallel
      const results = await Promise.allSettled([
        this.getOutlet(),
        this.getDailyOmsetByCategory()
      ])

      // Check for rejected promises
      const errors = results
        .filter(result => result.status === 'rejected')
        .map(result => result.reason)

      if (errors.length > 0) {
        const errorMessage =
          errors[0].message || 'Gagal memuat data. Silakan coba lagi.'
        this.error = errorMessage
        this.loading = false
        return
      }

      // Check if data is ready
      if (this.isDataReady) {
        // Add event listener
        window.addEventListener('afterprint', this.handleAfterPrint)

        // Trigger print after data is ready
        this.$nextTick(() => {
          this.triggerPrint()
        })
      } else {
        // Data not ready but no errors - might be empty data
        this.loading = false
      }
    } catch (error) {
      console.error('Error in mounted:', error)
      this.error = error.message || 'Gagal memuat data. Silakan coba lagi.'
      this.loading = false
    }
  },
  beforeDestroy() {
    // Clean up event listener
    window.removeEventListener('afterprint', this.handleAfterPrint)

    // Clear timeout if component unmounts before print
    if (this.printTimeoutId) {
      clearTimeout(this.printTimeoutId)
    }
  },
  methods: {
    /**
     * Validates required route query parameters
     * @returns {boolean} True if all required params are present and valid
     */
    validateRouteParams() {
      const requiredParams = ['start_date', 'end_date', 'outlet_id']
      const missingParams = requiredParams.filter(
        param => !this.$route.query[param]
      )

      if (missingParams.length > 0) {
        const errorMsg = `Parameter yang diperlukan tidak ditemukan: ${missingParams.join(
          ', '
        )}`
        this.error = errorMsg
        this.loading = false
        return false
      }

      // Validate date format
      const dateRegex = /^\d{4}-\d{2}-\d{2}$/
      if (
        !dateRegex.test(this.$route.query.start_date) ||
        !dateRegex.test(this.$route.query.end_date)
      ) {
        this.error = 'Format tanggal tidak valid. Gunakan format YYYY-MM-DD'
        this.loading = false
        return false
      }

      return true
    },

    /**
     * Fetches outlet information from API
     * @returns {Promise<void>}
     * @throws {Error} If API call fails
     */
    async getOutlet() {
      try {
        const response = await this.$http.get(
          `${process.env.VUE_APP_API_URL}/outlets/${this.$route.query.outlet_id}`
        )
        if (response.status === 200) {
          this.outlet = response.data.data.outlets
        }
      } catch (error) {
        console.error('Error fetching outlet:', error)
        const errorMessage =
          (error.response &&
            error.response.data &&
            error.response.data.message) ||
          'Gagal mengambil data outlet'
        throw new Error(errorMessage)
      }
    },
    /**
     * Fetches daily omset by category data from API
     * @returns {Promise<void>}
     * @throws {Error} If API call fails
     */
    async getDailyOmsetByCategory() {
      try {
        const response = await this.$http.get(
          `${process.env.VUE_APP_API_URL}/pos/dailyomset/by-category-parent?start_date=${this.$route.query.start_date}&end_date=${this.$route.query.end_date}`
        )
        if (response.status === 200) {
          this.categoryOmset = response.data.data.category_omset || []
          this.payment_method = response.data.data.payment || []
          this.totals = response.data.data.totals || {
            total_qty: 0,
            total_item_amount: 0,
            total_discount_item: 0,
            total_discount: 0,
            total_infaq: 0,
            total_round_up: 0,
            total_voucher: 0,
            total_omset: 0,
            total_transactions: 0
          }
          this.paginatedData = this.paginateCategories(
            this.categoryOmset,
            this.CATEGORIES_PER_PAGE
          )
          this.loading = false
        }
      } catch (error) {
        console.error('Error fetching daily omset:', error)
        const errorMessage =
          (error.response &&
            error.response.data &&
            error.response.data.message) ||
          'Gagal mengambil data omset by category'
        throw new Error(errorMessage)
      }
    },
    /**
     * Retries loading data after an error
     */
    retryLoadData() {
      this.error = null
      this.mounted()
    },

    /**
     * Triggers browser print dialog after ensuring content is ready
     */
    triggerPrint() {
      // Wait for DOM to be fully rendered
      this.$nextTick(() => {
        // Check if print element exists
        const printElement = document.getElementById('printMe')
        if (!printElement) {
          console.warn('Print element not found, retrying...')
          this.printTimeoutId = setTimeout(() => this.triggerPrint(), 500)
          return
        }

        // Trigger print with delay
        this.printTimeoutId = setTimeout(() => {
          window.print()
        }, this.PRINT_DELAY)
      })
    },

    /**
     * Handles after print event
     */
    handleAfterPrint() {
      // Clear timeout if still pending
      if (this.printTimeoutId) {
        clearTimeout(this.printTimeoutId)
      }
      window.close()
    },

    /**
     * Gets all subcategory rows from all categories for rendering in separate section
     * @param {Array} categories - Array of category objects (optional, uses all if not provided)
     * @returns {Array} Array of subcategory row objects
     */
    getSubcategoryRows(categories) {
      const rows = []
      const categoriesToProcess = categories || this.categoryOmset
      categoriesToProcess.forEach(category => {
        if (
          category.subcategories &&
          Array.isArray(category.subcategories) &&
          category.subcategories.length > 0
        ) {
          category.subcategories.forEach(subcat => {
            rows.push({
              category: category,
              subcategory: subcat
            })
          })
        }
      })
      return rows
    },

    /**
     * Gets all subcategory rows from all categories (for last page display)
     * @returns {Array} Array of subcategory row objects from all categories
     */
    getAllSubcategoryRows() {
      return this.getSubcategoryRows(this.categoryOmset)
    },

    /**
     * Paginates categories into pages for printing
     * @param {Array} categories - Array of category objects
     * @param {number} maxPerPage - Maximum categories per page
     * @returns {Array} Array of page objects with categories and startIndex
     */
    paginateCategories(categories, maxPerPage = 15) {
      const pages = []
      for (let i = 0; i < categories.length; i += maxPerPage) {
        const pageCategories = categories.slice(i, i + maxPerPage)
        pages.push({
          categories: pageCategories,
          startIndex: i
        })
      }
      return pages
    },

    /**
     * Formats number with custom decimal and thousands separators
     * @param {number} number - Number to format
     * @param {number} decimals - Number of decimal places
     * @param {string} dec_point - Decimal point character
     * @param {string} thousands_sep - Thousands separator character
     * @returns {string} Formatted number string
     */
    number_format(number, decimals, dec_point, thousands_sep) {
      number = (number + '').replace(/[^0-9+\-Ee.]/g, '')
      let n = !isFinite(+number) ? 0 : +number
      let prec = !isFinite(+decimals) ? 0 : Math.abs(decimals)
      let sep = typeof thousands_sep === 'undefined' ? ',' : thousands_sep
      let dec = typeof dec_point === 'undefined' ? '.' : dec_point
      let s = ''

      let toFixedFix = function (n, prec) {
        let k = Math.pow(10, prec)
        return '' + Math.round(n * k) / k
      }

      s = (prec ? toFixedFix(n, prec) : '' + Math.round(n)).split('.')
      if (s[0].length > 3) {
        s[0] = s[0].replace(/\B(?=(?:\d{3})+(?!\d))/g, sep)
      }
      if ((s[1] || '').length < prec) {
        s[1] = s[1] || ''
        s[1] += new Array(prec - s[1].length + 1).join('0')
      }

      return s.join(dec)
    },

    /**
     * Formats date using moment.js
     * @param {string} date - Date string to format
     * @returns {string} Formatted date string
     */
    dateFormatter(date) {
      return moment(date).format('DD MMMM YYYY')
    }
  }
}
</script>
<style scoped lang="scss">
table.detail-bon {
  width: 100%;
  border-collapse: collapse;
  border: 1px solid #000;
  font-family: inherit;
  font-size: 11px;
}
table.detail-bon th,
table.detail-bon td {
  border: 1px solid #000;
  padding: 6px 4px;
  text-align: center;
}
table.detail-bon th {
  background-color: #f0f0f0;
  font-weight: 600;
}
table.detail-bon tbody tr:nth-child(odd) {
  background-color: #fff;
}
table.detail-bon tbody tr:nth-child(even) {
  background-color: #f8f9fa;
}
.total-row {
  font-weight: 600;
  background-color: #e8e8e8;
}
.subcategory-header-row {
  background-color: #f0f0f0;
  font-weight: 600;
}
.subcategory-header {
  text-align: left;
  padding-left: 20px;
}
.subcategory-row {
  background-color: #fafafa;
}
.subcategory-indent {
  text-align: left;
  padding-left: 40px;
}
.outgoing-detail {
  width: 100%;
  margin-left: auto;
  margin-right: auto;
  box-sizing: border-box;
  overflow: hidden;
}
.border-header {
  border-top: 2px solid black;
  width: 82%;
  margin-top: -4vh;
  margin-left: auto;
  margin-right: auto;
  justify-content: center;
}
.title-header {
  margin-left: -5rem;
  line-height: 0.5;
}
.image {
  margin: auto;
  margin-left: 90px;
}
.title {
  text-align: center;
}
.detail-bon {
  margin-left: auto;
  margin-right: auto;
}
th,
td {
  padding: 5px;
}
.text-right {
  text-align: right;
}
/* Subcategories Section */
.subcategories-section {
  margin-top: 2rem;
  page-break-inside: avoid;
}

.subcategories-title {
  text-align: center;
  margin-bottom: 1rem;
  font-size: 14px;
  color: #333;
}

.subcategories-table {
  margin-top: 1rem;
}

.subcategories-table th {
  background-color: #e8e8e8;
  font-weight: 600;
}

.subcategories-table .subcategory-row {
  background-color: #fafafa;
}

.subcategories-table .subcategory-row:nth-child(even) {
  background-color: #f5f5f5;
}

.payment-summary {
  margin-top: 2rem;
  page-break-inside: avoid;
}
.summary-table {
  width: 100%;
  border-collapse: collapse;
  border: 1px solid #000;
  font-size: 12px;
  margin-top: 1rem;
}
.summary-table th,
.summary-table td {
  border: 1px solid #000;
  padding: 8px;
  text-align: left;
}
.summary-table th {
  background-color: #f0f0f0;
  font-weight: 600;
}
.summary-table td.text-right {
  text-align: right;
}
/* Loading State */
.loading-overlay {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  gap: 1rem;

  i {
    color: #007ad9;
  }

  p {
    color: #666;
    font-size: 1rem;
  }
}

/* Error State */
.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  gap: 1.5rem;
  padding: 2rem;

  .error-message {
    color: #c63737;
    font-size: 1.125rem;
    font-weight: 600;
    text-align: center;
  }
}

.text-muted {
  color: #999;
  font-style: italic;
}

@media print {
  .receipt-page {
    page-break-after: always;
  }
  .receipt-page:last-child {
    page-break-after: auto;
  }
  .payment-summary {
    page-break-inside: avoid;
  }
  /* Hide loading/error in print */
  .loading-overlay,
  .error-container {
    display: none !important;
  }
}
</style>
